import { useState } from "react";
import { AlertCircle, Check, Key, RefreshCw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  ConnectionRow,
  type NormalizedConnectionStatus,
} from "@/components/connection-row";
import { McpIcon } from "@/components/provider-icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type McpCredentialScope,
  type McpServer,
  useMcpServers,
} from "@/hooks/use-mcp-servers";

function serverStatus(server: McpServer): NormalizedConnectionStatus {
  const assignedAgents = server.agents.filter((agent) => agent.assigned);
  if (assignedAgents.length === 0) return "not_connected";
  return assignedAgents.every((agent) => agent.credential_configured)
    ? "connected"
    : "needs_attention";
}

function CredentialEditor({
  server,
  scope,
  configured,
  saving,
  saveCredential,
  deleteCredential,
}: {
  server: McpServer;
  scope: McpCredentialScope;
  configured: boolean;
  saving: boolean;
  saveCredential: (serverId: string, scope: McpCredentialScope, value: string) => Promise<boolean>;
  deleteCredential: (serverId: string, scope: McpCredentialScope) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const isPersonal = scope === "user";
  const label = isPersonal
    ? t("connections:mcpServers.personalCredential", { server: server.display_name })
    : t("connections:mcpServers.organizationCredential", { server: server.display_name });

  const handleSave = async () => {
    if (!value.trim()) return;
    if (await saveCredential(server.id, scope, value.trim())) setValue("");
  };

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <label className="text-sm font-medium" htmlFor={`${server.id}-${scope}-credential`}>
            {label}
          </label>
          <p className="text-xs text-muted-foreground">
            {isPersonal
              ? t("connections:mcpServers.personalCredentialDescription")
              : t("connections:mcpServers.organizationCredentialDescription")}
          </p>
        </div>
        <Badge variant={configured ? "secondary" : "outline"}>
          {configured
            ? t("connections:mcpServers.credentialConfigured")
            : t("connections:mcpServers.credentialNotConfigured")}
        </Badge>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={`${server.id}-${scope}-credential`}
          type="password"
          autoComplete="off"
          aria-label={label}
          placeholder={t("connections:mcpServers.credentialPlaceholder", { credential: server.credential_label })}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Button size="sm" onClick={() => void handleSave()} disabled={!value.trim() || saving}>
          {saving ? <RefreshCw className="animate-spin" /> : <Key />}
          {saving ? t("connections:actions.saving") : t("connections:mcpServers.saveCredential")}
        </Button>
      </div>
      {configured && (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => void deleteCredential(server.id, scope)}
          disabled={saving}
          aria-label={t("connections:mcpServers.removeCredentialAriaLabel", { scope: label })}
        >
          <Trash2 />
          {t("connections:mcpServers.removeCredential")}
        </Button>
      )}
    </div>
  );
}

function McpServerConnectionRow({
  server,
  saving,
  setAssignment,
  saveCredential,
  deleteCredential,
}: {
  server: McpServer;
  saving: boolean;
  setAssignment: (serverId: string, agentId: string, assigned: boolean) => Promise<boolean>;
  saveCredential: (serverId: string, scope: McpCredentialScope, value: string) => Promise<boolean>;
  deleteCredential: (serverId: string, scope: McpCredentialScope) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const status = serverStatus(server);
  const assignedAgents = server.agents.filter((agent) => agent.assigned);
  const statusDescription = (() => {
    if (assignedAgents.length === 0) return t("connections:mcpServers.noAgentsAssigned");
    if (status === "needs_attention") {
      return t("connections:mcpServers.assignedAgentsNeedCredential");
    }
    return t("connections:mcpServers.assignedAgents", { count: assignedAgents.length });
  })();

  return (
    <ConnectionRow
      providerId={`mcp-server:${server.id}`}
      icon={McpIcon}
      name={server.display_name}
      description={server.description}
      status={status}
      statusDescription={statusDescription}
      action={{ label: t("connections:actions.manage"), opensDetails: true }}
      loading={saving}
      details={(
        <div className="space-y-5">
          <div>
            <p className="text-sm font-medium">{t("connections:mcpServers.credentials")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{server.credential_help}</p>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <CredentialEditor
              server={server}
              scope="user"
              configured={server.credentials.user.configured}
              saving={saving}
              saveCredential={saveCredential}
              deleteCredential={deleteCredential}
            />
            <CredentialEditor
              server={server}
              scope="organization"
              configured={server.credentials.organization.configured}
              saving={saving}
              saveCredential={saveCredential}
              deleteCredential={deleteCredential}
            />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">{t("connections:mcpServers.assignedAgentsTitle")}</legend>
            {server.agents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("connections:mcpServers.noAssignableAgents")}
              </p>
            ) : (
              <div className="space-y-2">
                {server.agents.map((agent) => {
                  const scopeLabel = agent.kind === "personal"
                    ? t("connections:mcpServers.personalAgent")
                    : t("connections:mcpServers.sharedAgent");
                  const assignmentLabel = t("connections:mcpServers.assignAgent", {
                    server: server.display_name,
                    agent: agent.display_name,
                  });
                  return (
                    <label
                      key={agent.id}
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-md border bg-background p-3"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          checked={agent.assigned}
                          onChange={() => void setAssignment(server.id, agent.id, !agent.assigned)}
                          disabled={saving}
                          className="size-4 accent-primary"
                          aria-label={assignmentLabel}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{agent.display_name}</span>
                          <span className="block text-xs text-muted-foreground">{agent.id}</span>
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <Badge variant="outline">{scopeLabel}</Badge>
                        {agent.assigned && !agent.credential_configured && (
                          <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-300">
                            {t("connections:mcpServers.credentialMissing")}
                          </Badge>
                        )}
                        {agent.assigned && agent.credential_configured && (
                          <Check className="size-4 text-green-700 dark:text-green-400" aria-label={t("connections:mcpServers.credentialConfigured")} />
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </fieldset>
        </div>
      )}
    />
  );
}

export function McpServersPanel({ getToken }: { getToken: () => Promise<string> }) {
  const { t } = useTranslation();
  const {
    data,
    loading,
    saving,
    error,
    refresh,
    setAssignment,
    saveCredential,
    deleteCredential,
  } = useMcpServers(getToken);

  if (!data) {
    if (loading) {
      return (
        <div className="flex items-center gap-2 bg-card p-4 text-sm text-muted-foreground" aria-live="polite">
          <RefreshCw className="size-4 animate-spin" />
          {t("connections:mcpServers.loadingServers")}
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card p-4" role="alert">
          <span className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {t("connections:mcpServers.couldNotLoadServers")}
          </span>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            {t("connections:mcpServers.refreshServers")}
          </Button>
        </div>
      );
    }
    return (
      <p className="bg-card p-4 text-sm text-muted-foreground">
        {t("connections:mcpServers.noServers")}
      </p>
    );
  }

  return (
    <>
      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card p-4" role="alert">
          <span className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {error}
          </span>
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading || saving}>
            {t("connections:mcpServers.refreshServers")}
          </Button>
        </div>
      )}
      {data.servers.length === 0 ? (
        <p className="bg-card p-4 text-sm text-muted-foreground">{t("connections:mcpServers.noServers")}</p>
      ) : (
        data.servers.map((server) => {
          const localizedServer = server.id === "stripe"
            ? {
              ...server,
              display_name: t("connections:mcpServers.stripeDisplayName"),
              description: t("connections:mcpServers.stripeDescription"),
              credential_label: t("connections:mcpServers.stripeCredentialLabel"),
              credential_help: t("connections:mcpServers.stripeCredentialHelp"),
            }
            : server;

          return (
            <McpServerConnectionRow
              key={server.id}
              server={localizedServer}
              saving={saving}
              setAssignment={setAssignment}
              saveCredential={saveCredential}
              deleteCredential={deleteCredential}
            />
          );
        })
      )}
    </>
  );
}
